import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { switchMap } from 'rxjs/operators';
import { throwError, of, Observable } from 'rxjs';
import { OtpOption, SendOtpResponse } from '../models/otp-option';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {

  constructor(private api: ApiService) { }

  getChangePasswordOtpOptions(): Observable<OtpOption[]> {
    return this.api.get(`{
        passwordChangeOtpOptions{
          options
        }
      }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['passwordChangeOtpOptions']['options']);
      }),
      this.api.unifyErrors()
    );
  }

  sendPasswordChangeOtp(otpOption: OtpOption): Observable<SendOtpResponse> {
    return this.api.get(`mutation{
        sendPasswordChangeOtp(otpOption: ${otpOption}){
          ... on SendOtpResponse {
            success
            maskedPhoneNumber
            option
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
        }
      }`).pipe(
      switchMap(response => {
        const data = response['data']['sendPasswordChangeOtp'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  changePassword(currentPassword: string, newPassword: string, otpCode: string): Observable<boolean> {
    return this.api.get(`mutation changePassword($otpCode: String!, $newPassword: String!, $currentPassword: String!){
      changePassword(
        otpCode: $otpCode,
        newPassword: $newPassword,
        currentPassword: $currentPassword
      ){
        ... on GeneralResponse {
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      otpCode,
      newPassword,
      currentPassword
    }).pipe(
      switchMap(response => {
        const data = response['data']['changePassword'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  requestPasswordReset(email: string, captcha: string = null): Observable<boolean> {
    return this.api.get(`mutation{
      requestPasswordReset(email: "${email}"){
        ... on GeneralResponse {
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, null, false, false, captcha).pipe(
      switchMap(response => {
        const data = response['data']['requestPasswordReset'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      })
    );
  }

  getPasswordResetOtpOptions(email: string, token: string): Observable<OtpOption[]> {
    return this.api.get(`{
      passwordResetOtpOptions(
        email: "${email}",
        token: "${token}"
        ){
        options
      }
    }`, null, false).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['passwordResetOtpOptions']['options']);
      }),
      this.api.unifyErrors()
    );
  }

  sendPasswordResetOtp(email: string, token: string, otpOption: OtpOption): Observable<SendOtpResponse> {
    return this.api.get(`mutation{
      sendPasswordResetOtp(
        email: "${email}",
        token: "${token}"
        otpOption: ${otpOption}){
        ... on SendOtpResponse {
          success
          maskedPhoneNumber
          option
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, null, false).pipe(
      switchMap(response => {
        const data = response['data']['sendPasswordResetOtp'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  resetPassword(email: string, token: string, newPassword: string, otpCode: string): Observable<boolean> {
    return this.api.get(`mutation resetPassword($email: String!, $token: String!, $newPassword: String!, $otpCode: String){
      resetPassword(
        email: $email,
        token: $token,
        newPassword: $newPassword,
        otpCode: $otpCode
      ){
        ... on GeneralResponse {
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      email,
      token,
      newPassword,
      otpCode,
    }, false, false).pipe(
      switchMap(response => {
        const data = response['data']['resetPassword'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }
}
